/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { faBars, faTimes } from "@fortawesome/pro-regular-svg-icons";
import React, { useState } from "react";
import { Container, Modal, ModalBody, ModalHeader } from "reactstrap";
import { Button } from "../../../Helper/Button";
import Logo from "../Logo";
import * as styles from "./header.styles";
import { NavMainLinks } from "./NavMainLinks";
import { NavSecondaryLinks } from "./NavSecondaryLinks";
import { isClientPortal, isSupplierPortal } from "../../../utils";
import { useSecondaryLinks } from "../../../../hooks/useSecondaryLinks";

function NavToggle({ open, toggleNav, className }: { open: boolean; toggleNav(val: boolean): void; className?: string }) {
    return (
        <Button color="light" className={className} css={styles.navItem} onClick={() => toggleNav(!open)} icon={open ? faTimes : faBars} />
    );
}

const NavMobile = ({ children, isOpen, toggleNav }: { children: React.ReactNode; isOpen: boolean; toggleNav: any }) => (
    <Modal isOpen={isOpen} modalClassName="slide-in" backdropClassName="slide-in">
        <ModalHeader className="d-flex justify-content-end" css={css`height: 64px;`}>
            <NavToggle open={isOpen} toggleNav={toggleNav} className="ratio ratio-1x1"/>
        </ModalHeader>

        <ModalBody className="max-h-100 overflow-y-auto px-4">
            {children}
        </ModalBody>
    </Modal>
);

type HeaderWrapperProps = {
    mainLinks?: React.ReactNode;
    secondaryLinks?: React.ReactNode;
}

export const HeaderWrapper = ({ mainLinks, secondaryLinks }: HeaderWrapperProps) => {
    const [isNavOpen, toggleNav] = useState(false);
    const [isMobileNavOpen, toggleMobileNav] = useState(false);

    const hasMultipleLinks = (React.Children.count(mainLinks) + React.Children.count(secondaryLinks)) > 1;

    return (
        <div className="w-100">
            <div className="bg-white" css={css`box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.12); transition: color 0.3s ease-in;`}>
                <Container className="container d-flex align-items-center justify-content-between w-100" css={styles.headerWrapper}>

                    <div className={`${hasMultipleLinks ? "d-lg-none" : ""} w-100 bg-white`}>
                        <div className="w-100 d-flex py-2 justify-content-between align-items-center">
                            <Logo />

                            {hasMultipleLinks ? (
                                <NavToggle open={isMobileNavOpen} toggleNav={toggleMobileNav} className={isMobileNavOpen ? "opacity-0" : ""} />
                            ) : (
                                <div className="navbar py-0">
                                    <ul className="navbar-nav py-0 w-100">
                                        {mainLinks}
                                        {secondaryLinks}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {hasMultipleLinks && (
                            <NavMobile isOpen={isMobileNavOpen} toggleNav={toggleMobileNav}>
                                <div className="w-100">
                                    <div className="navbar py-0">
                                        <ul className="navbar-nav py-0 w-100">
                                            {mainLinks}
                                            {secondaryLinks}
                                        </ul>
                                    </div>
                                </div>
                            </NavMobile>
                        )}
                    </div>

                    {/* TABLET + DESKTOP NAV */}
                    {hasMultipleLinks && (
                        <div className="w-100 d-none d-lg-flex align-items-center justify-content-between">
                            <Logo />

                            <div className="w-100 d-flex align-items-center">
                                {isNavOpen ? (
                                    <div className="ms-auto">
                                        <div className="navbar navbar-expand-lg">
                                            <ul className="d-flex navbar-nav justify-content-end align-items-center w-100">
                                                {secondaryLinks}
                                            </ul>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="ms-auto">
                                        <div className="navbar navbar-expand-lg">
                                            <ul className="d-flex navbar-nav justify-content-end w-100">
                                                {mainLinks}
                                            </ul>
                                        </div>
                                    </div>
                                )}

                                <div className="ms-3">
                                    <NavToggle open={isNavOpen} toggleNav={toggleNav} />
                                </div>
                            </div>
                        </div>
                    )}
                </Container>
            </div>
        </div>
    );
};

export default function Header() {
    const isClientOrSupplierPortal = isClientPortal() || isSupplierPortal();
    const defaultSecondaryLinks = useSecondaryLinks();

    return (
        <HeaderWrapper
            mainLinks={isClientOrSupplierPortal ? null : <NavMainLinks />}
            secondaryLinks={isClientOrSupplierPortal ? defaultSecondaryLinks : <NavSecondaryLinks />}
        />
    );
}
